import { storeToRefs } from 'pinia'
import { useCommonStore } from '@/stores/common.ts'
import { useEditorStore } from '@/stores/editor.ts'
import { UseSelection } from '@/views/editor/composable/useSelection.ts'
import { deletePrefix, removeBackgroundSpan, handlerClose } from './useNoteUtils'
import { createBlockEvent } from './useBlockCreate'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import { getGlobalTypingHistoryManager } from '@/views/editor/composable/table/useTable.ts'
import { getEditorTypingHistoryManager } from '@/views/editor/composable/useEditorInput.ts'

/**
 * 삭제 버튼(Backspace, Delete) 이벤트 처리
 * @param e 키보드 이벤트
 */
export const deleteButtonEvent = (e: any) => {
	return new Promise<void>(async resolve => {
		const { socket } = storeToRefs(useCommonStore())
		const { noteUid, selector, noteType } = storeToRefs(useEditorStore())
		const selection = window.getSelection()
		const currentAnchorNode = selection?.anchorNode as HTMLElement

		if (!currentAnchorNode || !currentAnchorNode.parentNode) {
			resolve()
			return
		}

		const sel: any = window.getSelection()
		// 텍스트 노드가 아닌 경우 처리 (컨테이너 삭제 방지 등)
		if (sel.anchorNode.nodeType !== 3) {
			if (sel.anchorNode.parentNode.classList.contains('editor--container__content')) {
				e.preventDefault()
				resolve()
				return
			}
		}
		// 첫 번째 블록이 비어있을 때 삭제 방지
		if (sel.rangeCount > 0) {
			const range = sel.getRangeAt(0)
			const templateBlockList = [...document.querySelectorAll('.editor--block__container')]
			if (range.startOffset === 0 && templateBlockList[0].querySelector('.editor--block') === selector.value && selector.value.textContent === '') {
				e.preventDefault()
				resolve()
				return
			}
		}
		
		// 선택 영역이 있을 때 Backspace/Delete 입력 시 히스토리 저장
		if (sel.rangeCount > 0) {
			const range = sel.getRangeAt(0)
			
			if (!range.collapsed && (e.key === 'Backspace' || e.key === 'Delete')) {
				const history = new UseHistory()
				await history.push()
				
				// 선택 영역과 교차하는 모든 블록 찾기 (contenteditable="false" 블록도 감지)
				const allEditorBlocks = [...document.querySelectorAll('.editor--block')]
				const intersectedBlocks: HTMLElement[] = []
				for (const block of allEditorBlocks) {
					if (range.intersectsNode(block)) {
						intersectedBlocks.push(block as HTMLElement)
					}
				}

				const startBlock = intersectedBlocks[0] || null
				const endBlock = intersectedBlocks[intersectedBlocks.length - 1] || null

				if (startBlock && endBlock) {
					// 멀티 블록 선택인 경우 (2개 이상의 블록 선택)
					if (intersectedBlocks.length > 1) {
						const blocksArray = intersectedBlocks

						const hasNonTextBlock = blocksArray.some(block => {
							const type = block.dataset.type
							return type === 'defaultTable' || type === 'image' || type === 'file' || type === 'chart'
						})

						if (hasNonTextBlock) {
							e.preventDefault()

							let preservedBeforeHtml = ''
							let preservedAfterHtml = ''

							if (startBlock.dataset.type === 'text') {
								try {
									if (startBlock.contains(range.startContainer)) {
										const beforeRange = document.createRange()
										beforeRange.setStart(startBlock, 0)
										beforeRange.setEnd(range.startContainer, range.startOffset)
										const frag = document.createElement('div')
										frag.appendChild(beforeRange.cloneContents())
										preservedBeforeHtml = frag.innerHTML
									}
								} catch { /* range 조작 실패 시 빈 문자열 유지 */ }
							}

							if (endBlock !== startBlock && endBlock.dataset.type === 'text') {
								try {
									if (endBlock.contains(range.endContainer)) {
										const afterRange = document.createRange()
										afterRange.setStart(range.endContainer, range.endOffset)
										afterRange.setEnd(endBlock, endBlock.childNodes.length)
										const frag = document.createElement('div')
										frag.appendChild(afterRange.cloneContents())
										preservedAfterHtml = frag.innerHTML
									}
								} catch { /* range 조작 실패 시 빈 문자열 유지 */ }
							}

							let survivorBlock: HTMLElement | null = null
							const allBlockUidsToDelete: string[] = []

							if (startBlock.dataset.type === 'text') {
								survivorBlock = startBlock
								startBlock.innerHTML = preservedBeforeHtml + preservedAfterHtml

								for (let i = 1; i < blocksArray.length; i++) {
									const uid = deletePrefix(blocksArray[i].id)
									if (uid) allBlockUidsToDelete.push(uid)
									blocksArray[i].closest('.editor--block__container')?.remove()
								}
							} else if (endBlock.dataset.type === 'text') {
								survivorBlock = endBlock
								endBlock.innerHTML = preservedAfterHtml

								for (let i = 0; i < blocksArray.length - 1; i++) {
									const uid = deletePrefix(blocksArray[i].id)
									if (uid) allBlockUidsToDelete.push(uid)
									blocksArray[i].closest('.editor--block__container')?.remove()
								}
							} else {
								for (const block of blocksArray) {
									const uid = deletePrefix(block.id)
									if (uid) allBlockUidsToDelete.push(uid)
									block.closest('.editor--block__container')?.remove()
								}
							}

							if (noteType.value === 'Project') {
								if (allBlockUidsToDelete.length > 0) {
									socket.value.deleteNoteBlockList(noteUid.value, allBlockUidsToDelete)
								}
								if (survivorBlock && survivorBlock.isConnected) {
									const survivorUid = deletePrefix(survivorBlock.id)
									if (survivorUid) {
										socket.value.modifyNoteBlock(noteUid.value, survivorUid, survivorBlock.innerHTML, 'text')
									}
								}
							}

							const remaining = document.querySelectorAll('.editor--block__container')
							if (remaining.length < 1) {
								await createBlockEvent()
								survivorBlock = document.querySelector('.editor--block') as HTMLElement
							}

							if (survivorBlock && survivorBlock.isConnected) {
								survivorBlock.focus()
								selector.value = survivorBlock
								if (survivorBlock.dataset.type === 'text') {
									document.querySelectorAll('.editor--block').forEach(b => b.classList.remove('active'))
									survivorBlock.classList.add('active')

									const tmpDiv = document.createElement('div')
									tmpDiv.innerHTML = preservedBeforeHtml
									const cursorTextOffset = tmpDiv.textContent?.length || 0

									const newRange = document.createRange()
									const newSel = window.getSelection()
									let rangeSet = false
									let currentOffset = 0
									const walker = document.createTreeWalker(survivorBlock, NodeFilter.SHOW_TEXT, null)
									let nd: Node | null
									while ((nd = walker.nextNode())) {
										const len = nd.textContent?.length || 0
										if (currentOffset + len >= cursorTextOffset) {
											newRange.setStart(nd, cursorTextOffset - currentOffset)
											rangeSet = true
											break
										}
										currentOffset += len
									}
									if (!rangeSet) {
										newRange.setStart(survivorBlock, survivorBlock.childNodes.length)
									}
									newRange.collapse(true)
									newSel?.removeAllRanges()
									newSel?.addRange(newRange)
								}
							}

							resolve()
							return
						}

						// 텍스트 블록만 있는 경우: 기존 MutationObserver 기반 처리
						const blockUidsToDelete: string[] = []

						for (let i = 1; i < blocksArray.length; i++) {
							const blockUid = deletePrefix(blocksArray[i].id)
							if (blockUid) {
								blockUidsToDelete.push(blockUid)
							}
						}

						if (blockUidsToDelete.length > 0 && noteType.value === 'Project') {
							const container: any = document.querySelector('.editor--container__content')
							const firstBlockUid = deletePrefix(startBlock.id)

							const observer = new MutationObserver(() => {
								setTimeout(() => {
									socket.value.deleteNoteBlockList(noteUid.value, blockUidsToDelete)

									if (firstBlockUid && startBlock.isConnected) {
										const firstBlockContent = startBlock.innerHTML
										const firstBlockType = startBlock.dataset.type || 'text'
										socket.value.modifyNoteBlock(noteUid.value, firstBlockUid, firstBlockContent, firstBlockType)
									}

									observer.disconnect()
								}, 0)
							})
							observer.observe(container, { childList: true, subtree: true })

							setTimeout(() => {
								observer.disconnect()
								socket.value.deleteNoteBlockList(noteUid.value, blockUidsToDelete)

								if (firstBlockUid && startBlock.isConnected) {
									const firstBlockContent = startBlock.innerHTML
									const firstBlockType = startBlock.dataset.type || 'text'
									socket.value.modifyNoteBlock(noteUid.value, firstBlockUid, firstBlockContent, firstBlockType)
								}
							}, 500)
						}
					}
				}
				
				resolve()
				return
			}
		}
		
		// 명시적 블록 삭제/병합 처리
		if (sel.rangeCount > 0) {
			const range = sel.getRangeAt(0)
			const useSelection = new UseSelection()

			// 커서가 블록의 맨 앞에 있는지 정확하게 판단 (<br> 태그 및 보이지 않는 문자 고려)
			let isAtStart = false
			if (range.collapsed) {
				// 블록이 비어있으면 커서 위치와 무관하게 항상 시작 위치로 간주
				// (브라우저가 빈 블록에 <br>을 자동 삽입하여 offset이 1이 될 수 있음)
				const blockVisibleText = selector.value.textContent?.replace(/[\u200B\s]/g, '') || ''
				if (blockVisibleText === '') {
					isAtStart = true
				}
				// startContainer가 블록 자체이고 offset이 0
				else if (range.startContainer === selector.value && range.startOffset === 0) {
					isAtStart = true
				}
				// startContainer가 블록의 첫 번째 자식이고 offset이 0
				else if (range.startContainer === selector.value.firstChild && range.startOffset === 0) {
					isAtStart = true
				}
				// startContainer가 텍스트 노드이고 offset이 0이며, 이전에 텍스트 노드가 없음
				else if (range.startContainer.nodeType === Node.TEXT_NODE && range.startOffset === 0) {
					// 이전 형제 노드가 없거나, 모두 빈 노드인 경우
					let prevNode = range.startContainer.previousSibling
					let hasContentBefore = false
					while (prevNode) {
						// <br> 태그는 내용으로 간주하지 않음 (브라우저 자동 삽입 태그)
						if (prevNode.nodeType === Node.ELEMENT_NODE && (prevNode as HTMLElement).tagName === 'BR') {
							prevNode = prevNode.previousSibling
							continue
						}
						// zero-width space 및 빈 텍스트 노드 무시
						if (prevNode.nodeType === Node.TEXT_NODE && prevNode.textContent && prevNode.textContent.replace(/[\u200B\s]/g, '').length > 0) {
							hasContentBefore = true
							break
						}
						// 실제 내용이 있는 요소인 경우
						if (prevNode.nodeType === Node.ELEMENT_NODE && (prevNode as HTMLElement).textContent && (prevNode as HTMLElement).textContent!.replace(/[\u200B\s]/g, '').length > 0) {
							hasContentBefore = true
							break
						}
						prevNode = prevNode.previousSibling
					}
					if (!hasContentBefore) {
						isAtStart = true
					}
				}
				// startContainer가 <br> 요소이고 이전에 내용이 없는 경우도 start로 간주
				else if (range.startContainer.nodeType === Node.ELEMENT_NODE && (range.startContainer as HTMLElement).tagName === 'BR') {
					let prevNode = range.startContainer.previousSibling
					let hasContentBefore = false
					while (prevNode) {
						if (prevNode.nodeType === Node.ELEMENT_NODE && (prevNode as HTMLElement).tagName === 'BR') {
							prevNode = prevNode.previousSibling
							continue
						}
						if (prevNode.nodeType === Node.TEXT_NODE && prevNode.textContent && prevNode.textContent.replace(/[\u200B\s]/g, '').length > 0) {
							hasContentBefore = true
							break
						}
						if (prevNode.nodeType === Node.ELEMENT_NODE && (prevNode as HTMLElement).textContent && (prevNode as HTMLElement).textContent!.replace(/[\u200B\s]/g, '').length > 0) {
							hasContentBefore = true
							break
						}
						prevNode = prevNode.previousSibling
					}
					if (!hasContentBefore) {
						isAtStart = true
					}
				}
			}

			const isAtEnd = useSelection.isCursorAtBlockEnd(selector.value)

			let shouldHandleExplicitly = false
			let targetPrevBlock: HTMLElement | null = null
			let targetNextBlock: HTMLElement | null = null

			// Backspace: 커서가 맨 앞 + 이전 블록 존재
			if (e.key === 'Backspace' && isAtStart) {
				targetPrevBlock = selector.value.parentElement?.previousElementSibling?.querySelector('.editor--block') as HTMLElement
				if (targetPrevBlock) {
					if (targetPrevBlock.getAttribute('data-avatar-locked') === 'true') {
						e.preventDefault()
						resolve()
						return
					}
					shouldHandleExplicitly = true
				}
			}
			
			// Delete: 커서가 맨 끝 + 다음 블록 존재
			if (e.key === 'Delete' && isAtEnd) {
				targetNextBlock = selector.value.parentElement?.nextElementSibling?.querySelector('.editor--block') as HTMLElement
				if (targetNextBlock) {
					if (targetNextBlock.getAttribute('data-avatar-locked') === 'true') {
						e.preventDefault()
						resolve()
						return
					}
					shouldHandleExplicitly = true
				}
			}
			
			if (shouldHandleExplicitly) {
				e.preventDefault()
				
				// 히스토리 저장 (블록 삭제/병합 전)
				const history = new UseHistory()
				await history.push()
				
				// Backspace: 현재 블록 삭제 또는 이전 블록에 병합
				if (e.key === 'Backspace' && targetPrevBlock) {
					const currentBlockContent = selector.value.innerHTML
					const prevBlockEndLength = targetPrevBlock.textContent?.length || 0
					
					// 현재 블록이 비어있으면 삭제, 아니면 병합
					// <br>만 있거나 보이는 텍스트가 없는 경우도 빈 블록으로 간주
					const isCurrentBlockEmpty = currentBlockContent.trim() === '' ||
						currentBlockContent.trim() === '<br>' ||
						(selector.value.textContent?.replace(/[\u200B\s]/g, '') || '') === ''
					if (isCurrentBlockEmpty) {
						// 빈 블록 삭제
						const blockContainer = selector.value.closest('.editor--block__container')
						const blockUid = deletePrefix(selector.value.id)
						
						if (blockContainer) {
							blockContainer.remove()
						}
						
						// 소켓 삭제 요청
						if (noteType.value === 'Project') {
							socket.value.deleteNoteBlock(noteUid.value, blockUid)
						}
						
						// 이전 블록으로 포커스 이동
						targetPrevBlock.focus()
						selector.value = targetPrevBlock
						
						// focus/focusout 이벤트 순서에 의해 active가 제거될 수 있으므로 명시적으로 보장
						if (targetPrevBlock.dataset.type === 'text') {
							document.querySelectorAll('.editor--block').forEach(b => b.classList.remove('active'))
							targetPrevBlock.classList.add('active')
						}
						
						// 커서를 끝으로 이동
						const newRange = document.createRange()
						const newSelection = window.getSelection()
						if (targetPrevBlock.lastChild) {
							const lastNode = targetPrevBlock.lastChild
							if (lastNode.nodeType === Node.TEXT_NODE) {
								newRange.setStart(lastNode, lastNode.textContent?.length || 0)
							} else {
								newRange.setStart(targetPrevBlock, targetPrevBlock.childNodes.length)
							}
						} else {
							newRange.setStart(targetPrevBlock, 0)
						}
						newRange.collapse(true)
						newSelection?.removeAllRanges()
						newSelection?.addRange(newRange)
					} else {
						// 이전 블록에 병합
					// 이전 블록에 병합
					const tempDiv = document.createElement('div')
					tempDiv.innerHTML = currentBlockContent
					
					// .editor--block 요소가 있으면 제거 (중첩 방지)
					const nestedBlocks = tempDiv.querySelectorAll('.editor--block')
					nestedBlocks.forEach(block => {
						const parent = block.parentNode
						while (block.firstChild) {
							parent?.insertBefore(block.firstChild, block)
						}
						block.remove()
					})
					
					// background가 적용된 span 제거
					const spans = tempDiv.querySelectorAll('span[style*="background"]')
					spans.forEach(span => {
						const textContent = span.textContent || ''
						const textNode = document.createTextNode(textContent)
						span.parentNode?.replaceChild(textNode, span)
					})
					
					// trailing <br> 태그 제거 (브라우저 자동 삽입 또는 shift+enter로 인한 <br>)
					const childNodesArray = Array.from(tempDiv.childNodes)
					while (childNodesArray.length > 0) {
						const lastNode = childNodesArray[childNodesArray.length - 1]
						if (lastNode.nodeType === Node.ELEMENT_NODE && (lastNode as HTMLElement).tagName === 'BR') {
							childNodesArray.pop()
						} else if (lastNode.nodeType === Node.TEXT_NODE && (!lastNode.textContent || lastNode.textContent.replace(/\u200B/g, '').trim() === '')) {
							// trailing zero-width space나 빈 텍스트 노드도 제거
							childNodesArray.pop()
						} else {
							break
						}
					}
					
					// 이전 블록에 내용 추가
					childNodesArray.forEach(node => {
						targetPrevBlock!.appendChild(node.cloneNode(true))
					})
						
						// 현재 블록 삭제
						const blockContainer = selector.value.closest('.editor--block__container')
						const blockUid = deletePrefix(selector.value.id)
						
						if (blockContainer) {
							blockContainer.remove()
						}
						
						// 소켓 삭제 요청
						if (noteType.value === 'Project') {
							socket.value.deleteNoteBlock(noteUid.value, blockUid)
						}
						
						// 이전 블록으로 포커스 이동
						targetPrevBlock.focus()
						targetPrevBlock.classList.add('active')
						selector.value = targetPrevBlock
						
						// 커서를 병합 지점으로 이동
						const newRange = document.createRange()
						const newSelection = window.getSelection()
						let offset = prevBlockEndLength
						
						// 텍스트 노드 찾기
						let rangeSet = false
						let currentOffset = 0
						const walker = document.createTreeWalker(targetPrevBlock, NodeFilter.SHOW_TEXT, null)
						let node: Node | null
						while ((node = walker.nextNode())) {
							const textLength = node.textContent?.length || 0
							if (currentOffset + textLength >= offset) {
								newRange.setStart(node, offset - currentOffset)
								rangeSet = true
								break
							}
							currentOffset += textLength
						}
						
						if (!rangeSet) {
							newRange.setStart(targetPrevBlock, targetPrevBlock.childNodes.length)
						}
						
						newRange.collapse(true)
						newSelection?.removeAllRanges()
						newSelection?.addRange(newRange)
					}
				}
				
				// Delete: 다음 블록을 현재 블록에 병합
				if (e.key === 'Delete' && targetNextBlock) {
					const currentBlockEndLength = selector.value.textContent?.length || 0
					const nextBlockContent = targetNextBlock.innerHTML
					
					const tempDiv = document.createElement('div')
					tempDiv.innerHTML = nextBlockContent
					
					// background가 적용된 span 제거
					const spans = tempDiv.querySelectorAll('span[style*="background"]')
					spans.forEach(span => {
						const textContent = span.textContent || ''
						const textNode = document.createTextNode(textContent)
						span.parentNode?.replaceChild(textNode, span)
					})
					
					// 현재 블록에 내용 추가
					Array.from(tempDiv.childNodes).forEach(node => {
						selector.value.appendChild(node.cloneNode(true))
					})
					
					// 다음 블록 삭제
					const nextBlockContainer = targetNextBlock.closest('.editor--block__container')
					const nextBlockUid = deletePrefix(targetNextBlock.id)
					
					if (nextBlockContainer) {
						nextBlockContainer.remove()
					}
					
					// 소켓 삭제 요청
					if (noteType.value === 'Project') {
						socket.value.deleteNoteBlock(noteUid.value, nextBlockUid)
					}
					
					// 커서를 병합 지점으로 이동
					const newRange = document.createRange()
					const newSelection = window.getSelection()
					let offset = currentBlockEndLength
					
					// 텍스트 노드 찾기
					let rangeSet = false
					let currentOffset = 0
					const walker = document.createTreeWalker(selector.value, NodeFilter.SHOW_TEXT, null)
					let node: Node | null
					while ((node = walker.nextNode())) {
						const textLength = node.textContent?.length || 0
						if (currentOffset + textLength >= offset) {
							newRange.setStart(node, offset - currentOffset)
							rangeSet = true
							break
						}
						currentOffset += textLength
					}
					
					if (!rangeSet) {
						newRange.setStart(selector.value, selector.value.childNodes.length)
					}
					
					newRange.collapse(true)
					newSelection?.removeAllRanges()
					newSelection?.addRange(newRange)
				}
				
				resolve()
				return
			}
		}
		


		const container: any = document.querySelector('.editor--container__content')
		let prevElement: any
		// Backspace인 경우 이전 블록 찾기
		if (e.key === 'Backspace') {
			prevElement = selector.value.parentElement.previousElementSibling?.querySelector('.editor--block')
		}
		const prevElementChildCount = prevElement ? prevElement.childNodes.length : 0

		let isResolved = false
		let timeoutId: ReturnType<typeof setTimeout> | null = null

		const cleanup = () => {
			if (timeoutId !== null) {
				clearTimeout(timeoutId)
				timeoutId = null
			}
			observer.disconnect()
		}

		// MutationObserver로 새로 생성된 div 추적 (삭제 후 병합 처리 감지)
		const observer = new MutationObserver(mutations => {
			mutations.forEach(mutation => {
				mutation.removedNodes.forEach(async (node: Node) => {
					let tempNode: any = node
					const newDiv: any = tempNode as HTMLDivElement
					if (newDiv.nodeType === 1 && newDiv.tagName === 'DIV') {
						const targetDiv = newDiv.classList.contains('editor--block')
							? newDiv
							: newDiv.querySelector('.editor--block')
								? newDiv.querySelector('.editor--block')
								: newDiv.closest('.editor--block__container').querySelector('.editor--block')

						if (!targetDiv) return
						const targetId = targetDiv.id
						if (targetId && document.getElementById(targetId)) {
							return
						}
						const blockUid = deletePrefix(targetDiv.id)
						// 프로젝트 타입인 경우 소켓 삭제 요청
						if (noteType.value === 'Project') {
							socket.value.deleteNoteBlock(noteUid.value, deletePrefix(blockUid))
						}

						// 병합으로 prevElement에 붙은 내용 중 background가 적용된 span 래퍼 및 trailing <br> 제거
						if (prevElement) {
							prevElement?.classList.add('active')
							selector.value = prevElement

							const total = prevElement.childNodes.length
							for (let i = prevElementChildCount; i < total; i++) {
								const appendedNode = prevElement.childNodes[i]
								removeBackgroundSpan(appendedNode)
							}
							
							// trailing <br> 태그 제거
							while (prevElement.lastChild) {
								const lastChild = prevElement.lastChild
								if (lastChild.nodeType === Node.ELEMENT_NODE && (lastChild as HTMLElement).tagName === 'BR') {
									prevElement.removeChild(lastChild)
								} else if (lastChild.nodeType === Node.TEXT_NODE && (!lastChild.textContent || lastChild.textContent.replace(/\u200B/g, '').trim() === '')) {
									prevElement.removeChild(lastChild)
								} else {
									break
								}
							}
						} else if (e.key === 'Delete') {
							// Delete 키인 경우 현재 블록에서 병합된 내용 처리
							const total = selector.value.childNodes.length
							for (let i = prevElementChildCount; i < total; i++) {
								const appendedNode = selector.value.childNodes[i]
								removeBackgroundSpan(appendedNode)
							}
							
							// trailing <br> 태그 제거
							while (selector.value.lastChild) {
								const lastChild = selector.value.lastChild
								if (lastChild.nodeType === Node.ELEMENT_NODE && (lastChild as HTMLElement).tagName === 'BR') {
									selector.value.removeChild(lastChild)
								} else if (lastChild.nodeType === Node.TEXT_NODE && (!lastChild.textContent || lastChild.textContent.replace(/\u200B/g, '').trim() === '')) {
									selector.value.removeChild(lastChild)
								} else {
									break
								}
							}
						}
						if (!isResolved) {
							isResolved = true
							cleanup()
							resolve()
						}
					}
				})
			})
		})

		observer.observe(container, { childList: true, subtree: true })

		// 안전장치: 500ms 후 옵저버 해제
		timeoutId = setTimeout(() => {
			if (!isResolved) {
				isResolved = true
				cleanup()
				resolve()
			}
		}, 500)
	})
}

/**
 * 블록 삭제 로직 (메뉴 등을 통한 삭제)
 */
export const deleteBlock = async () => {
	const { socket } = storeToRefs(useCommonStore())
	const { noteUid, enterSelector, noteType } = storeToRefs(useEditorStore())

	const tableTypingManager = getGlobalTypingHistoryManager()
	if (tableTypingManager) tableTypingManager.cleanup()
	const editorTypingManager = getEditorTypingHistoryManager()
	if (editorTypingManager) editorTypingManager.cleanup()

	const history = new UseHistory()

	let enterSelectorNext = enterSelector.value.nextElementSibling

	// 다음/이전 형제 요소 찾기 (블록 또는 컨테이너)
	if (!enterSelectorNext) {
		enterSelectorNext = enterSelector.value.previousElementSibling
	}

	if (!enterSelectorNext) {
		enterSelectorNext = enterSelector.value.closest('.editor--block__container').nextElementSibling
	}

	if (!enterSelectorNext) {
		enterSelectorNext = enterSelector.value.closest('.editor--block__container').previousElementSibling
	}

	if (noteType.value === 'Project') {
		await socket.value.deleteNoteBlock(noteUid.value, deletePrefix(enterSelector.value.id))
	}

	const editorBlockList = [...document.querySelectorAll('.editor--block')]
	enterSelector.value.closest('.editor--block__container').remove()

	// 모든 블록이 삭제된 경우 새 블록 생성
	if (editorBlockList.length === 1) {
		await createBlockEvent()
		return
	}

	// 포커스 이동
	if (enterSelectorNext && enterSelectorNext.querySelector('.editor--block')) {
		enterSelectorNext.querySelector('.editor--block').focus()
	} else if (enterSelectorNext) {
		enterSelectorNext.focus()
	}

	// DOM 삭제 후 push → block-delete delta가 undo 스택에 기록됨
	await history.push()

	handlerClose()
}
